import { Injectable } from '@angular/core';
import { Functions, httpsCallableData } from '@angular/fire/functions';
import { Observable } from 'rxjs';
import { AuthService } from '../../auth.service';
import { CallableNames, DbService } from '../../db.service';
import { ILecturerSessionData } from '../../store/sessions-lecturer-store.service';
import { ISessionCardData } from '../../store/session-card-store.service';
import { SessionFormat } from 'src/app/shared/enums/session-group/session-formats.enum';
import { ISession } from 'src/app/shared/models/session-group/session.model';
import { SessionLecturerState } from 'src/app/shared/enums/session-group/session-lecturer-states.enum';
import { INote } from 'src/app/shared/models/_other/note.model';
import { IPrice } from 'src/app/shared/models/price/price.model';
import { ISingleSessionAttendanceFE, IUserSessionAttendanceFE } from 'src/app/shared/models/session-attendance/session-attendance.model';

export interface ISessionsLecturerFilterData {
  year?: number;
  month?: number;
};

interface ICallablesSessionsGetForCardData {
  sessionUuid: string;
}

export interface ICallablesSessionsUpdateData {
  id: number;

  title: string | null;
  startAt: string;
  endAt: string;
  format: SessionFormat;
  meetingUrl: string | null;
  sessionLecturerAttendanceTags: string[];
  duration: number;
  hourDuration: number;

  locationId: number | null;
}

interface ICallablesSiModulesSessionCreateSessionData {
  shopItemId: number;
  format: SessionFormat;

  // only if format is on site
  locationId?: number;
  startAt?: Date;
  endAt?: Date;
  // /
}

interface ICallablesSessionsArchiveData {
  id: number;
}

interface ICallablesSessionsCopyData {
  id: number;

  count: number;
  step: number;
}

export interface ICallablesSessionsGetForLocationPickerData {
  locationIds: number[];
  startAt: string;
  endAt: string;
}
export type ICallablesSessionsGetForLocationPickerResult = {
  locations: {
    id: number;
    title: string;
    internalTitle: string | null;
    locationGroup: {
      id: number;
      title: string;
      external: boolean;
    } | null;
    sessions: {
      id: number;
      title: string | null;
      startAt: string;
      endAt: string;
      shopItem: {
        id: number;
        title: string;
      };
    }[];
  }[]
};

interface ICallablesSessionsGetForLecturerScheduleData {
  orgId: number;
  targetDates: string[];
}
export type ICallablesSessionsGetForLecturerScheduleResult = {
  id: number;
  uuid: string;
  title: string | null;
  index: number;
  startAt: string;
  endAt: string;
  format: SessionFormat;

  sessionLecturers: {
    id: number;
    state: SessionLecturerState | null;
    lecturer: {
      id: number;
      userId: number;
    }
  }[];
  shopItem: {
    title: string;
    quantity: number;
  };
  location: {
    title: string;
    internalTitle: string | null;
  } | null;
  participantsCnt: number;
};

interface ICallablesSessionsGetForLecturerScheduleIndicatorsData {
  orgId: number;
  targetDates: string[];
}
type ICallablesSessionsGetForLecturerScheduleIndicatorsResult = {
  id: number;
  startAt: string;
  endAt: string;
  sessionLecturers: {
    id: number;
    state: SessionLecturerState | null;
  }[];
}[];

interface ICallablesSessionsGetForSessionLecturerAttendanceInputGroupData {
  sessionId: number;
}
export interface ICallablesSessionsGetForSessionLecturerAttendanceInputGroupResult {
  session: {
    id: number;
    uuid: string;
    startAt: string;
    endAt: string;
    hourDuration: number;
    title: string | null;
    lecturerAttendanceClosed: boolean;
    sessionLecturers: {
      id: number;
      lecturer: {
        id: number;
        userId: number;
      }
    }[];
  };
  userSessionLecturer: {
    id: number;
    state: SessionLecturerState | null;
    lecturingWith: number[];
    notes: INote[];
    otherExpenses: IPrice | null;
    hourRate: IPrice | null;
  } | null;
}

interface ICallablesSessionsGetForSessionDetailCardModalData {
  sessionUuid?: string;
  sessionId?: number;
}
export interface ICallablesSessionsGetForSessionDetailCardModalResult {
  id: number;
  uuid: string;
  title: string | null;
  index: number;
  startAt: Date;
  endAt: Date;
  format: SessionFormat;
  meetingUrl: string | null;

  shopItem: {
    id: number;
    uuid: string;
    title: string;
  };
  location: {
    id: number;
    title: string;
    internalTitle: string | null;
    mapUrl: string | null;
  } | null;

  sessionAttendances: ISingleSessionAttendanceFE[];
}

interface ICallablesSessionsGetUpcomingByLecturerData {
  orgId: number;
}

@Injectable({
  providedIn: 'root'
})
export class SessionsService {

  constructor(
    private dbService: DbService,
  ) { }

  public getUpcomingSessionForLecturer(data: ICallablesSessionsGetUpcomingByLecturerData): Observable<ISession | null> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesSessionsGetUpcomingByLecturer, data });
    return this.dbService.handleObs(obs, { succSilent: true });
  }

  public getForSessionDetailCardModal(data: ICallablesSessionsGetForSessionDetailCardModalData): Observable<ICallablesSessionsGetForSessionDetailCardModalResult> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesSessionsGetForSessionDetailCardModal, data });
    return this.dbService.handleObs(obs, { succSilent: true });
  }

  public getForSessionLecturerAttendanceInputGroup(data: ICallablesSessionsGetForSessionLecturerAttendanceInputGroupData): Observable<ICallablesSessionsGetForSessionLecturerAttendanceInputGroupResult> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesSessionsGetForSessionLecturerAttendanceInputGroup, data });
    return this.dbService.handleObs(obs, { succSilent: true });
  }

  public getForLecturerScheduleIndicators(data: ICallablesSessionsGetForLecturerScheduleIndicatorsData): Observable<ICallablesSessionsGetForLecturerScheduleIndicatorsResult> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesSessionsGetForLecturerScheduleIndicators, data });
    return this.dbService.handleObs(obs, { succSilent: true });
  }
  public getForLecturerSchedule(data: ICallablesSessionsGetForLecturerScheduleData): Observable<ICallablesSessionsGetForLecturerScheduleResult[]> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesSessionsGetForLecturerSchedule, data });
    return this.dbService.handleObs(obs, { succSilent: true });
  }

  public getForLocationPicker(data: ICallablesSessionsGetForLocationPickerData): Observable<ICallablesSessionsGetForLocationPickerResult> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesSessionsGetForLocationPicker, data });
    return this.dbService.handleObs(obs, { succSilent: true });
  }

  public copySession(data: ICallablesSessionsCopyData) {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesSessionsCopy, data });
    return this.dbService.handleObs(obs);
  }

  public archiveSession(data: ICallablesSessionsArchiveData): Observable<number> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesSessionsArchive, data });
    return this.dbService.handleObs(obs);
  }

  public siModuleSessions_createSession(data: ICallablesSiModulesSessionCreateSessionData): Observable<ISession> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesSiModuleSessionsCreateSession, data });
    return this.dbService.handleObs(obs);
  }

  public updateSession(data: ICallablesSessionsUpdateData): Observable<ISession> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesSessionsUpdate, data });
    return this.dbService.handleObs(obs);
  }

  public getDataForSessionCard(data: ICallablesSessionsGetForCardData): Observable<ISessionCardData> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesSessionsGetForCard, data });
    return this.dbService.handleObs(obs, { succSilent: true });
  }

  public getLecturerSessions(orgId: number, admin: boolean, filters?: ISessionsLecturerFilterData): Observable<ILecturerSessionData[]> {
    const obs = this.dbService.runCallable({ name: CallableNames.callablesSessionsGetMonthList, data: { orgId, admin, filters } });
    return this.dbService.handleObs(obs, { succSilent: true });
  }

}
